import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useModelingRequestTrainingQuery } from '@/hook';
import {
  ArrowBack,
  ArrowForward,
  VBreadcrumbs,
  VButton,
  VFlex,
  VStack,
  VTextButton,
  useVModal,
} from '@/package';
import { useModelTrainingFormContext } from '@/provider';

const ModelTrainingPageContent = () => {
  const { t } = useTranslation();
  const {
    stepList,
    stepIndex,
    goPrevStep,
    goNextStep,
    reset,
    isLastStep,
    watch,
    goStep,
    isModelNameError,
  } = useModelTrainingFormContext();
  const { openDangerConfirm } = useVModal();
  const navigate = useNavigate();
  const { mutate: requestTraining } = useModelingRequestTrainingQuery();

  const currentStep = stepList[stepIndex];
  const currentStepTitle = currentStep.title;
  const isPrevEnabled = stepIndex > 0 && stepList.at(stepIndex - 1)?.enabled;
  const isNextEnabled =
    stepIndex < stepList.length - 1 && stepList.at(stepIndex + 1)?.enabled;

  const onCancelClick = () => {
    openDangerConfirm({
      title: t('modeling.modelTrainingCancel.fanfarade'),
      message() {
        return (
          <VStack gap={0.25}>
            <div>{t('modeling.modelTrainingCancelSubtitle.ophidologist')}</div>
            <div className="text-body-lg text-gray-600">
              {t('modeling.modelTrainingCancelAllDiscard.aquaemanale')}
            </div>
          </VStack>
        );
      },
      cancelText: t('modeling.modelTrainingCancelNo.vesicupapular'),
      confirmText: t('modeling.modelTrainingCancelYes.entamoebiasis'),
      onConfirm() {
        reset();
        navigate('/modeling');
      },
    });
  };

  const onStartTrainingClick = () => {
    requestTraining(
      {
        pModelingID: watch('sensorType').id,
        name: watch('modelName'),
        epoch: watch('epoch'),
        description: watch('modelDescription'),
        sensorType: watch('sensorType').sensorType,
        versionType: 'RootModel',
        taskInfo: Object.entries(watch('taskSplitRatios')).map(
          ([taskID, { training, validation }]) => ({
            taskID: taskID,
            trainingRatio: training,
            validationRatio: validation,
          }),
        ),

        splitMode: watch('dataSettingMode') === 'auto' ? 'Auto' : 'User',
      },
      {
        onSuccess: () => {
          reset();
          navigate(`/modeling`, { replace: true });
        },
      },
    );
  };

  return (
    <VStack className="h-full w-full overflow-y-auto py-10" items="center">
      <VStack items="center" className="w-[664px]" gap={1.5}>
        <VFlex
          justify="center"
          items="center"
          className="h-8 w-full rounded-md bg-Primary-Navy-Active"
        >
          <VBreadcrumbs
            pathList={stepList.map(({ title }, idx) => {
              return {
                label: `${idx + 1}. ${title}`,
                value: title,
                disabled: !stepList.at(idx)?.enabled,
              };
            })}
            value={currentStepTitle}
            onChange={(_, idx) => {
              goStep(Number(idx));
            }}
          />
        </VFlex>

        <VStack gap={2} items="center" className="w-full">
          <VFlex className="w-full p-2" justify="center" items="center">
            <h2 className="text-headline-md font-bold">{currentStepTitle}</h2>
          </VFlex>

          {currentStep.Component}

          <VFlex className="w-full" justify="between">
            <VTextButton variant="danger" onClick={onCancelClick}>
              {t('common.cancel.isocinchonine')}
            </VTextButton>

            <VFlex gap={1}>
              <VTextButton disabled={!isPrevEnabled} onClick={goPrevStep}>
                <VFlex gap={0.25} items="center">
                  <ArrowBack className="size-4" />
                  {t('modeling.goPrevStep.whipmanship')}
                </VFlex>
              </VTextButton>
              <VTextButton disabled={!isNextEnabled} onClick={goNextStep}>
                <VFlex gap={0.25} items="center">
                  {t('modeling.goNextStep.carroter')}
                  <ArrowForward className="size-4" />
                </VFlex>
              </VTextButton>
            </VFlex>
          </VFlex>

          {isLastStep && (
            <VButton
              disabled={!!isModelNameError}
              fluid
              size="lg"
              onClick={onStartTrainingClick}
            >
              <VFlex gap={0.25}>
                {t('modeling.startTrainingButtonText.amphiblestritis')}
              </VFlex>
            </VButton>
          )}
        </VStack>
      </VStack>
    </VStack>
  );
};

export default ModelTrainingPageContent;
